import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../../lib/format';
import type {
  DashboardTrends, PlatformActivity, PlatformOverview, PlatformPipeline, UpcomingCamp,
} from '@dru/types';
import { Button, ErrorState, StatCard, TextSkeleton, cn } from '@dru/ui';
import { Activity as ActivityIcon, Building2, FileBadge, Hospital, Inbox, Stethoscope, UsersRound } from 'lucide-react';
import { DonutChart, TrendChart, UpcomingCamps } from '../../../components/charts';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';

/** Aggregate platform totals, case pipeline and recent activity. */
export function Overview() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const can = {
    audit: holds(permissions, 'audit.read'),
    leads: holds(permissions, ['lead.read', '*']),
    companies: holds(permissions, 'tenant.read'),
    cases: holds(permissions, 'case.read-ops'),
    partners: holds(permissions, 'partner.read'),
    physicians: holds(permissions, 'examination.route'),
  };

  const ov = useQuery<PlatformOverview>(() => api.get('/platform/overview'), []);
  const pipeline = useQuery<PlatformPipeline>(() => api.get('/admin/dashboard/pipeline'), []);

  // Each of these is asked only of a role that may read it: asking and
  // rendering a 403 would teach people errors are normal.
  const activity = useQuery<PlatformActivity | null>(
    () => (can.audit ? api.get<PlatformActivity>('/admin/dashboard/activity?limit=8') : Promise.resolve(null)),
    [can.audit],
  );
  const leads = useQuery<{ byStatus: Record<string, number>; unassigned: number; followUpOverdue: number } | null>(
    () => (can.leads ? api.get('/leads/summary') : Promise.resolve(null)),
    [can.leads],
  );
  const openLeads = ['NEW', 'CONTACTED', 'QUALIFIED']
    .reduce((n, k) => n + (leads.data?.byStatus?.[k] ?? 0), 0);

  // The year's shape and the camp calendar, summed across companies the
  // same way the tiles are. Two calls, so a slow trend does not hold the
  // table and either can be retried alone.
  const trends = useQuery<DashboardTrends>(() => api.get('/admin/dashboard/trends'), []);
  const camps = useQuery<{ items: UpcomingCamp[] }>(() => api.get('/admin/dashboard/upcoming-camps?limit=10'), []);

  if (ov.error) return <ErrorState detail={ov.error} onRetry={ov.reload} />;

  const d = ov.data;
  const pendingRegistrations = d?.tenants.PENDING ?? 0;
  return (
    <div className="space-y-5">
      <PageHeader
        className="mb-0"
        title="Platform overview"
        hint={d ? `Platform totals, case pipeline and recent activity. Counts as of ${formatDateTime(d.generatedAt)}.` : 'Platform totals, case pipeline and recent activity.'}
        actions={(
          <Button variant="secondary" size="sm" onClick={() => { ov.reload(); pipeline.reload(); activity.reload(); leads.reload(); trends.reload(); camps.reload(); }}>
            Refresh
          </Button>
        )}
      />

      {/* The one onboarding job only this console does: self-registered
          companies wait for a review, and this count is the server's. */}
      {can.companies && pendingRegistrations > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-dueSoon-600/30 bg-dueSoon-100 px-4 py-3">
          <div className="min-w-0">
            <div className="text-body font-semibold text-neutral-900">
              {pendingRegistrations} company {pendingRegistrations === 1 ? 'registration is' : 'registrations are'} waiting for approval
            </div>
            <p className="text-small text-neutral-700">Registered from the public sign-up form. Review the details, then approve or reject.</p>
          </div>
          <Button onClick={() => nav('/admin/companies?status=PENDING')}>Review registrations</Button>
        </div>
      )}

      <section aria-labelledby="ov-figures" className="space-y-2">
        <h2 id="ov-figures" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">The platform</h2>
        {/* Number cards (the 29 Oct 2026 layout): an icon in a navy square,
            the number, and a footer line with an arrow when it opens a list. */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {([
            {
              key: 'companies', label: 'Active companies', value: d?.tenants.ACTIVE ?? 0, loading: ov.loading,
              hint: joinLine(pendingRegistrations ? `${pendingRegistrations} awaiting approval` : 'None awaiting approval',
                month(d, 'companiesRegistered', 'registered')),
              onClick: can.companies ? () => nav('/admin/companies?status=ACTIVE') : undefined,
            },
            {
              // The workforce, named as what it is. This tile used to read
              // "Workers covered" over the head-count — 38, while the
              // companies' own dashboards said 8 were covered. Coverage is
              // the second figure: active workers holding a current
              // certificate, counted by the rule the companies' compliance
              // summary uses.
              key: 'workers', label: 'Active employees', value: d?.employees.toLocaleString('en-IN') ?? 0, loading: ov.loading,
              hint: !d ? undefined
                : month(d, 'employeesAdded', 'added', true)
                  ?? (d.employeesWithCurrentCertificate === undefined
                    ? `On the rolls of ${d.companiesCounted} ${d.companiesCounted === 1 ? 'company' : 'companies'}`
                    : `${d.employeesWithCurrentCertificate.toLocaleString('en-IN')} ${d.employeesWithCurrentCertificate === 1 ? 'holds' : 'hold'} a current certificate`),
            },
            {
              key: 'cases', label: 'Cases in progress', value: pipeline.data?.inFlight ?? 0, loading: pipeline.loading,
              hint: joinLine(pipeline.data?.breached ? `${pipeline.data.breached} past their promised date` : 'None past their promised date',
                month(d, 'casesRaised', 'raised')),
              tone: pipeline.data?.breached ? 'overdue' : undefined,
              onClick: can.cases ? () => nav('/ops/cases') : undefined,
            },
            {
              key: 'certs', label: 'Valid certificates', value: d?.certificates.ACTIVE ?? 0, loading: ov.loading,
              hint: joinLine(d?.certificates.expiringWithin90Days ? `${d.certificates.expiringWithin90Days} expire within 90 days` : 'None expiring within 90 days',
                month(d, 'certificatesIssued', 'issued')),
            },
            can.leads && {
              key: 'leads', label: 'Open enquiries', value: openLeads, loading: leads.loading,
              hint: 'New, contacted or qualified',
              onClick: () => nav('/admin/leads'),
            },
            {
              key: 'partners', label: 'Empanelled partners', value: d?.network.partners ?? 0, loading: ov.loading,
              hint: 'Hospitals and laboratories',
              onClick: can.partners ? () => nav('/admin/partners') : undefined,
            },
            {
              key: 'physicians', label: 'Certifying physicians', value: d?.network.physicians ?? 0, loading: ov.loading,
              hint: 'On the panel',
              onClick: can.physicians ? () => nav('/admin/physicians') : undefined,
            },
          ] as Array<false | { key: string; label: string; value: React.ReactNode; hint?: React.ReactNode; tone?: 'covered' | 'dueSoon' | 'overdue' | 'pending'; onClick?: () => void; loading?: boolean }>)
            .filter((f): f is Exclude<typeof f, false> => !!f)
            .map((f) => (
              <StatCard key={f.key} label={f.label} value={f.value} sub={f.hint} valueTone={f.tone}
                        loading={f.loading} onClick={f.onClick} icon={FIGURE_ICON[f.key]} />
            ))}
        </div>
      </section>

      <Charts trends={trends} camps={camps} onOpenCompany={can.companies ? (id) => nav(`/admin/companies/${id}`) : undefined} />

      <Pipeline data={pipeline.data} loading={pipeline.loading} error={pipeline.error} onRetry={pipeline.reload} />

      {can.audit && <Activity data={activity.data} loading={activity.loading} error={activity.error} onRetry={activity.reload} />}
    </div>
  );
}

/**
 * "+3 added this month", optionally "· 1 by now last month" — the API counts
 * the same number of days at the start of last month, so a month still
 * running is never compared with a whole one. Undefined until the API sends it.
 */
function month(d: PlatformOverview | null | undefined, key: 'companiesRegistered' | 'employeesAdded' | 'certificatesIssued' | 'casesRaised', verb: string, compare = false) {
  const now = d?.thisMonth?.[key];
  if (now == null) return undefined;
  const line = `+${now.toLocaleString('en-IN')} ${verb} this month`;
  return compare ? `${line} · ${(d?.lastMonthSameDays?.[key] ?? 0).toLocaleString('en-IN')} by now last month` : line;
}
const joinLine = (a: string, b?: string) => (b ? `${a} · ${b}` : a);

const FIGURE_ICON: Record<string, React.ReactNode> = {
  companies: <Building2 />, workers: <UsersRound />, cases: <ActivityIcon />, certs: <FileBadge />,
  leads: <Inbox />, partners: <Hospital />, physicians: <Stethoscope />,
};

/**
 * The year behind the tiles: raised and signed off by month, the companies
 * by the service they mostly run, and the next camps across the platform.
 */
function Charts({ trends, camps, onOpenCompany }: {
  trends: { data: DashboardTrends | null; loading: boolean; error: string | null; reload: () => void };
  camps: { data: { items: UpcomingCamp[] } | null; loading: boolean; error: string | null; reload: () => void };
  onOpenCompany?: (tenantId: string) => void;
}) {
  const t = trends.data;
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <section aria-labelledby="ov-trend" className="min-w-0 rounded-card border border-neutral-200 bg-white p-4 shadow-card lg:col-span-2">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="ov-trend" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Health checkup trend</h2>
          <span className="text-caption text-neutral-500">Examinations raised by type, signed off, and treatments</span>
        </div>
        <TrendChart months={t?.months} loading={trends.loading} error={trends.error} onRetry={trends.reload} />
      </section>

      <section aria-labelledby="ov-mix" className="min-w-0 rounded-card border border-neutral-200 bg-white p-4 shadow-card">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="ov-mix" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Companies by service</h2>
          <span className="text-caption text-neutral-500">The type each company mostly ran</span>
        </div>
        <DonutChart items={t?.mix.items} total={t?.mix.total ?? 0} unit="companies" loading={trends.loading} error={trends.error} onRetry={trends.reload} />
      </section>

      <section aria-labelledby="ov-camps" className="min-w-0 rounded-card border border-neutral-200 bg-white shadow-card lg:col-span-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-4">
          <h2 id="ov-camps" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Upcoming camps</h2>
          <span className="text-caption text-neutral-500">Campaigns starting today or later, soonest first</span>
        </div>
        <UpcomingCamps
          items={camps.data?.items} loading={camps.loading} error={camps.error} onRetry={camps.reload} showCompany
          onOpen={onOpenCompany ? (c) => { if (c.company) onOpenCompany(c.company.id); } : undefined}
        />
      </section>
    </div>
  );
}

/**
 * Light navy to dark along the way, and green for the finish: the order of
 * the journey reads from the colour alone, and "certified" is the one that
 * means done.
 */
const STAGE_BG = ['bg-primary-200', 'bg-primary-300', 'bg-primary-400', 'bg-primary-500', 'bg-primary-600', 'bg-primary-700', 'bg-primary-800'];
const stageBg = (i: number, n: number, key: string) =>
  key === 'CERTIFIED' || i === n - 1 ? 'bg-success-600' : STAGE_BG[Math.min(i, STAGE_BG.length - 1)]!;

/**
 * The case pipeline as a journey (owner, 29 Oct 2026: "make it creative"):
 * one bar showing where the cases in hand sit, and the stages as a track of
 * numbered steps with their counts. Empty stages fade back; late ones say so.
 */
function Pipeline({ data, loading, error, onRetry }: {
  data?: PlatformPipeline | null; loading: boolean; error: string | null; onRetry: () => void;
}) {
  const stages = data?.stages ?? [];
  const total = stages.reduce((t, s) => t + s.count, 0);
  return (
    <section aria-labelledby="ov-pipeline" className="rounded-card border border-neutral-200 bg-white p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="ov-pipeline" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
          Case pipeline
          {data && <span className="ml-2 font-normal normal-case tracking-normal text-neutral-500">· {data.inFlight} in flight</span>}
        </h2>
        {data && (
          <span className={cn('text-caption', data.breached > 0 ? 'font-medium text-overdue-600' : 'text-neutral-500')}>
            {data.breached > 0 ? `${data.breached} past their promised date` : 'All within their promised dates'}
          </span>
        )}
      </div>

      {error ? (
        <div className="mt-3"><ErrorState detail={error} onRetry={onRetry} /></div>
      ) : loading ? (
        <div className="mt-4 space-y-4" aria-busy="true">
          <div className="h-3 animate-pulse rounded-full bg-neutral-100" />
          <div className="grid grid-cols-4 gap-3 xl:grid-cols-8">
            {Array.from({ length: 8 }, (_, i) => <div key={i} className="h-20 animate-pulse rounded-input bg-neutral-100" />)}
          </div>
        </div>
      ) : (
        <>
          {/* Where the cases sit, as shares of one bar. */}
          {total > 0 && (
            <div className="chart-grow-x mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full bg-neutral-100" aria-hidden>
              {stages.map((s, i) => s.count > 0 && (
                <div key={s.key} title={`${s.label}: ${s.count}`}
                  className={cn('h-full first:rounded-l-full last:rounded-r-full', stageBg(i, stages.length, s.key))}
                  style={{ width: `${(s.count / total) * 100}%` }} />
              ))}
            </div>
          )}

          {/* The stages as a track of numbered steps. Scrolls sideways on a phone. */}
          <div className="-mx-1 mt-4 overflow-x-auto px-1 pb-1">
            <ol className="relative grid min-w-[720px] grid-cols-8 gap-2">
              <span aria-hidden className="absolute left-[6.25%] right-[6.25%] top-4 h-0.5 rounded-full bg-neutral-200" />
              {stages.map((s, i) => {
                const empty = s.count === 0;
                return (
                  <li key={s.key} className="chart-rise relative flex flex-col items-center text-center" style={{ animationDelay: `${i * 70}ms` }}>
                    <span className={cn('relative z-[1] flex h-8 w-8 items-center justify-center rounded-full text-caption font-semibold ring-4 ring-white',
                      empty ? 'bg-neutral-100 text-neutral-500' : cn(stageBg(i, stages.length, s.key), i < 2 && s.key !== 'CERTIFIED' ? 'text-primary-800' : 'text-white'))}>
                      {i + 1}
                    </span>
                    <span className={cn('mt-2 text-h4 font-semibold tabular-nums', empty ? 'text-neutral-400' : 'text-neutral-900')}>{s.count}</span>
                    <span className={cn('text-caption font-medium leading-tight', empty ? 'text-neutral-400' : 'text-neutral-600')}>{s.label}</span>
                    {s.breached > 0 && (
                      <span className="mt-1 rounded-full bg-overdue-100 px-2 py-0.5 text-micro font-medium text-overdue-600">{s.breached} late</span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </section>
  );
}

function Activity({ data, loading, error, onRetry }: {
  data?: PlatformActivity | null; loading: boolean; error: string | null; onRetry: () => void;
}) {
  return (
    <section aria-labelledby="ov-activity" className="rounded-card border border-neutral-200 bg-white shadow-card">
      <div className="flex items-baseline justify-between gap-3 border-b border-neutral-200 px-4 py-3">
        <h2 id="ov-activity" className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Recent activity</h2>
      </div>

      {error ? (
        <div className="p-4"><ErrorState detail={error} onRetry={onRetry} /></div>
      ) : loading ? (
        <div className="p-4"><TextSkeleton lines={3} /></div>
      ) : (data?.items.length ?? 0) === 0 ? (
        <p className="px-4 py-6 text-caption text-neutral-500">Nothing recorded recently.</p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {data!.items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-4 py-2.5 text-caption">
              <span className="min-w-0">
                <span className="font-medium text-neutral-900">{r.label}</span>
                {r.actorRole && <span className="ml-2 font-normal text-neutral-500">{roleWords(r.actorRole)}</span>}
                {r.outcome !== 'SUCCESS' && (
                  <span className="ml-2 font-medium text-overdue-600">{r.outcome.toLowerCase()}</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums font-normal text-neutral-500">{formatDateTime(r.occurredAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function roleWords(key: string): string {
  const s = key.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
